import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Check, Plus, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import type {
  ReschedulePollResponse,
  RescheduleVoteValue,
  SeasonMatchResponse,
} from '@bm/contracts';
import { api, ApiError } from '../../lib/api';
import { Alert, Button, Field } from '../ui';
import { Modal } from '../Modal';
import { responseButtonStyle } from '../../lib/response-button-style';
import { useTheme } from '../../lib/theme';
import { genderBgClass, genderTextClass } from '../../lib/gender-colors';

/** Every date/time field and the delete button in this modal share this
 * height — native `<input type="date">` and `type="time">` render at
 * different intrinsic heights across browsers even with identical padding,
 * which looked visibly mismatched (see git history). An explicit height
 * fixes both to the same box regardless of that native rendering quirk.
 * Deliberately compact (32px, not the usual 44px touch-target height) —
 * this form is dense (up to 10 proposal rows) and read/scanned far more
 * than precisely tapped, so favoring compactness over generous tap area
 * is the right tradeoff here specifically. */
const FIELD_HEIGHT = 'h-8';
const FIELD_CLASS = `${FIELD_HEIGHT} w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent`;

function formatOptionDate(date: string, time: string | null): string {
  const label = new Date(`${date}T00:00:00`).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return time ? `${label} · ${time.slice(0, 5)} Uhr` : label;
}

/** One candidate date/time — used both in "Neue Abstimmung starten" (multi-row,
 * `removable` toggles per row) and in the single-row "Termin hinzufügen" for an
 * already-running poll (`removable={false}`). Labeled Datum/Uhrzeit fields
 * (native date/time inputs can't show a real placeholder — a label says what
 * belongs in the field instead) stacked two-up, with the remove button
 * pinned to the same height and bottom edge as the inputs themselves.
 * `removable=false` omits the button entirely rather than just disabling it
 * — a disabled-but-visible trash icon (the "Termin hinzufügen" row, or the
 * lone default proposal row) read as broken, and reserving its width for
 * nothing was also why these fields came out narrower than "Geeinigt auf"'s
 * (which never had a button) even though both should match. */
function ProposalRow({
  value,
  onChange,
  onRemove,
  removable,
}: {
  value: { date: string; time: string };
  onChange: (v: { date: string; time: string }) => void;
  onRemove: () => void;
  removable: boolean;
}) {
  return (
    <div className="flex items-end gap-2">
      <div className="grid flex-1 grid-cols-2 gap-2">
        <Field label="Datum">
          <input
            type="date"
            required
            value={value.date}
            onChange={(e) => onChange({ ...value, date: e.target.value })}
            className={FIELD_CLASS}
          />
        </Field>
        <Field label="Uhrzeit (optional)">
          <input
            type="time"
            value={value.time}
            onChange={(e) => onChange({ ...value, time: e.target.value })}
            className={FIELD_CLASS}
          />
        </Field>
      </div>
      {removable && (
        <button
          type="button"
          title="Vorschlag entfernen"
          onClick={onRemove}
          className={`flex ${FIELD_HEIGHT} w-8 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600`}
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

/** Small numbered circle for the step-by-step layout below — filled/checked
 * once that step is done, otherwise a plain outlined number. Purely visual
 * (steps stay fully usable out of order, e.g. a captain can set the agreed
 * date directly without ever running a poll), so this never disables
 * anything — it just shows progress at a glance. */
function StepCircle({ n, done }: { n: number; done: boolean }) {
  return (
    <div
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
        done ? 'bg-green-600 text-white' : 'bg-brand-accent text-white'
      }`}
    >
      {done ? <Check size={14} /> : n}
    </div>
  );
}

/** One step of the "Abstimmen → Geeinigt auf" wizard — its own card (light
 * gray background) with a numbered circle + heading up top, content below.
 * Two separate cards rather than a connected timeline, so each phase reads
 * as its own distinct area. */
function Step({
  n,
  done,
  title,
  children,
}: {
  n: number;
  done: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl bg-slate-50 p-4">
      <div className="mb-3 flex items-center gap-3">
        <StepCircle n={n} done={done} />
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      </div>
      {children}
    </div>
  );
}

/** Verlegungsassistent — Mannschaftsführer (matches.manage) stellen mehrere
 * Termin-Vorschläge zur Abstimmung, jeder Spieler (matches.respond) stimmt je
 * Vorschlag mit Daumen hoch/runter ab; ist man sich einig, trägt ein
 * Mannschaftsführer den vereinbarten Termin ein. Siehe docs/season.md.
 * Presented as two numbered cards (1 Abstimmen, 2 Geeinigt auf) rather than
 * a flat stack of sections — the two phases of the workflow, visually in
 * the order a captain actually works through them. */
export function RescheduleModal({ match, onClose }: { match: SeasonMatchResponse; onClose: () => void }) {
  const qc = useQueryClient();
  // Vereinsweite Berechtigung ODER additiv Mannschaftsführer für die
  // Mannschaft dieses Spiels — server-berechnet, siehe
  // SeasonMatchResponse.canManage / docs/permissions.md.
  const canManage = match.canManage;
  const queryKey = ['season', 'reschedule', match.id];

  const { data: poll } = useQuery({
    queryKey,
    queryFn: () => api<ReschedulePollResponse | null>(`/season/matches/${match.id}/reschedule`, { orgScoped: true }),
  });
  const { theme } = useTheme();

  const [proposals, setProposals] = useState<Array<{ date: string; time: string }>>([{ date: '', time: '' }]);
  const [newOption, setNewOption] = useState({ date: '', time: '' });
  // "Termin hinzufügen" starts collapsed (just the button) — the Datum-/
  // Uhrzeit-Felder only appear once the button is actually clicked, instead
  // of sitting empty and unexplained under an already-populated
  // Terminvorschläge list.
  const [addingOption, setAddingOption] = useState(false);
  const [agreeDate, setAgreeDate] = useState('');
  const [agreeTime, setAgreeTime] = useState('');

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey });
    void qc.invalidateQueries({ queryKey: ['season', 'matches'] });
  };

  const startPoll = useMutation({
    mutationFn: () =>
      api<ReschedulePollResponse>(`/season/matches/${match.id}/reschedule`, {
        method: 'POST',
        orgScoped: true,
        body: { options: proposals.filter((p) => p.date).map((p) => ({ date: p.date, time: p.time || undefined })) },
      }),
    onSuccess: () => {
      setProposals([{ date: '', time: '' }]);
      invalidate();
    },
  });

  // Additive Alternative zu "Neue Abstimmung starten" — fügt EINEN weiteren
  // Vorschlag zur laufenden Abstimmung hinzu, ohne bestehende
  // Vorschläge/Stimmen zu verwerfen.
  const addOption = useMutation({
    mutationFn: () =>
      api(`/season/matches/${match.id}/reschedule/options`, {
        method: 'POST',
        orgScoped: true,
        body: { date: newOption.date, time: newOption.time || undefined },
      }),
    onSuccess: () => {
      setNewOption({ date: '', time: '' });
      setAddingOption(false);
      invalidate();
    },
  });

  // Gegenstück zu addOption — entfernt einen einzelnen Terminvorschlag aus
  // der laufenden Abstimmung. Auch der letzte verbleibende Vorschlag darf
  // weg: der Server löscht dann die ganze Abstimmung mit (Reset zurück in
  // den "keine Abstimmung läuft"-Zustand), statt eine offene Abstimmung mit
  // null Vorschlägen übrig zu lassen — `invalidate()` holt danach einfach
  // `poll: null` vom Server, kein Sonderfall hier nötig.
  const removeOption = useMutation({
    mutationFn: (optionId: string) =>
      api(`/season/matches/${match.id}/reschedule/options/${optionId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: invalidate,
  });

  const vote = useMutation({
    mutationFn: (v: { optionId: string; vote: RescheduleVoteValue; memberId: string }) =>
      api(`/season/matches/${match.id}/reschedule/vote`, {
        method: 'POST',
        orgScoped: true,
        body: v,
      }),
    onSuccess: invalidate,
  });

  const agree = useMutation({
    mutationFn: () =>
      api(`/season/matches/${match.id}/reschedule/agree`, {
        method: 'POST',
        orgScoped: true,
        body: { date: agreeDate, time: agreeTime || undefined },
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  // Gegenstück zu `agree` — für den Fall, dass falsch geklickt oder sich
  // doch anders entschieden wurde. Setzt das Spiel zurück auf "geplant",
  // der rote "Verlegt"-Hinweis verschwindet wieder. Bleibt im Modal (kein
  // `onClose()`), damit man direkt einen neuen Termin eintragen kann, falls
  // gewünscht.
  const unagree = useMutation({
    mutationFn: () => api(`/season/matches/${match.id}/reschedule/agree`, { method: 'DELETE', orgScoped: true }),
    onSuccess: invalidate,
  });

  // See TrainingPage.tsx's identical `showNames` for the reasoning — a lone
  // child still needs a name, a lone "you" doesn't.
  const showNames = (poll?.respondableMembers ?? []).some((m) => !m.isOwn);
  const pollIsOpen = poll?.status === 'open';
  const proposalsValid = proposals.some((p) => p.date);
  const step2Done = match.status === 'postponed' && !!match.rescheduledDate;
  const step1Done = step2Done || poll?.status === 'closed';

  return (
    <Modal
      title="Verlegungsassistent"
      subtitle={`${match.homeTeam ?? '—'} vs. ${match.awayTeam ?? '—'}`}
      icon={<ArrowLeftRight size={18} />}
      onClose={onClose}
      maxWidthClassName="max-w-xl"
    >
      <div className="space-y-4">
        {match.status === 'postponed' && match.rescheduledDate && (
          <Alert kind="success">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>Verlegt auf den {formatOptionDate(match.rescheduledDate, match.rescheduledTime)}.</span>
              {canManage && (
                <Button
                  type="button"
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  disabled={unagree.isPending}
                  onClick={() => unagree.mutate()}
                >
                  {unagree.isPending ? 'Wird zurückgenommen …' : 'Rückgängig machen'}
                </Button>
              )}
            </div>
          </Alert>
        )}
        {unagree.isError && (
          <Alert>{unagree.error instanceof ApiError ? unagree.error.message : 'Konnte nicht zurückgenommen werden.'}</Alert>
        )}

        <Step n={1} done={step1Done} title="Abstimmen">
          {canManage && !pollIsOpen && (
            <div className="mb-4">
              {poll && (
                <p className="mb-2 text-xs text-slate-500">
                  Die letzte Abstimmung ist abgeschlossen — eine neue beginnt frisch.
                </p>
              )}
              <div className="space-y-2">
                {proposals.map((p, i) => (
                  <ProposalRow
                    key={i}
                    value={p}
                    removable={proposals.length > 1}
                    onChange={(v) => setProposals((cur) => cur.map((x, xi) => (xi === i ? v : x)))}
                    onRemove={() => setProposals((cur) => cur.filter((_, xi) => xi !== i))}
                  />
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="gap-1.5 px-2.5 py-1.5 text-xs"
                  disabled={proposals.length >= 10}
                  onClick={() => setProposals((cur) => [...cur, { date: '', time: '' }])}
                >
                  <Plus size={13} /> Weiterer Vorschlag
                </Button>
                <Button
                  type="button"
                  className="px-3 py-1.5 text-xs"
                  disabled={!proposalsValid || startPoll.isPending}
                  onClick={() => startPoll.mutate()}
                >
                  {startPoll.isPending ? 'Wird gestartet …' : 'Abstimmung starten'}
                </Button>
              </div>
              {startPoll.isError && (
                <Alert>{startPoll.error instanceof ApiError ? startPoll.error.message : 'Konnte nicht gestartet werden.'}</Alert>
              )}
            </div>
          )}

          {poll && (
            <div>
              <p className="mb-2 text-xs font-medium text-slate-500">
                Terminvorschläge {poll.status === 'closed' && <span className="font-normal text-slate-400">(abgeschlossen)</span>}
              </p>
              <div className="space-y-2">
                {poll.options.map((o) => (
                  <div key={o.id} className="rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-4">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800">{formatOptionDate(o.date, o.time)}</span>
                      <span className="flex items-center gap-2.5 text-xs text-slate-500">
                        <span className="flex items-center gap-1 text-green-600">
                          <ThumbsUp size={12} /> {o.yesCount}
                        </span>
                        <span className="flex items-center gap-1 text-red-500">
                          <ThumbsDown size={12} /> {o.noCount}
                        </span>
                        {canManage && pollIsOpen && (
                          <button
                            type="button"
                            title="Terminvorschlag entfernen"
                            disabled={removeOption.isPending}
                            onClick={() => removeOption.mutate(o.id)}
                            className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </span>
                    </div>
                    {o.voters.length > 0 && (
                      <div className="mb-1.5 flex flex-wrap gap-1">
                        {o.voters.map((v) => (
                          <span
                            key={v.memberId}
                            className={`rounded px-1.5 py-0.5 text-xs font-medium ${genderBgClass(v.gender)} ${
                              v.vote === 'no' ? 'text-red-600 line-through' : genderTextClass(v.gender)
                            }`}
                          >
                            {v.firstName}
                          </span>
                        ))}
                      </div>
                    )}
                    {poll.respondableMembers.map((m) => {
                      const myVote = m.votes[o.id] ?? null;
                      const isYes = myVote === 'yes';
                      const isNo = myVote === 'no';
                      const dim = (active: boolean) => (myVote !== null && !active ? { opacity: 0.3 } : {});
                      return (
                        <div key={m.memberId} className="flex items-center gap-1">
                          {showNames && (
                            <span className="mr-1 max-w-20 truncate text-xs text-slate-500">{m.name}</span>
                          )}
                          <Button
                            variant="ghost"
                            title="Kann ich"
                            aria-label="Kann ich"
                            disabled={!pollIsOpen || vote.isPending}
                            className="leading-none transition hover:brightness-95 active:brightness-90 disabled:hover:brightness-100"
                            style={{
                              ...responseButtonStyle('accepted', isYes, theme),
                              ...dim(isYes),
                            }}
                            onClick={() => vote.mutate({ optionId: o.id, vote: 'yes', memberId: m.memberId })}
                          >
                            <ThumbsUp size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            title="Kann ich nicht"
                            aria-label="Kann ich nicht"
                            disabled={!pollIsOpen || vote.isPending}
                            className="leading-none transition hover:brightness-95 active:brightness-90 disabled:hover:brightness-100"
                            style={{
                              ...responseButtonStyle('declined', isNo, theme),
                              ...dim(isNo),
                            }}
                            onClick={() => vote.mutate({ optionId: o.id, vote: 'no', memberId: m.memberId })}
                          >
                            <ThumbsDown size={14} />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {canManage && pollIsOpen && (
                <div className="mt-2">
                  {addingOption ? (
                    <>
                      <ProposalRow
                        value={newOption}
                        removable={false}
                        onChange={setNewOption}
                        onRemove={() => setNewOption({ date: '', time: '' })}
                      />
                      <div className="mt-1.5 flex items-center gap-2">
                        <Button
                          type="button"
                          className="px-3 py-1.5 text-xs"
                          disabled={!newOption.date || addOption.isPending}
                          onClick={() => addOption.mutate()}
                        >
                          {addOption.isPending ? 'Wird hinzugefügt …' : 'Hinzufügen'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="px-2.5 py-1.5 text-xs"
                          disabled={addOption.isPending}
                          onClick={() => {
                            setAddingOption(false);
                            setNewOption({ date: '', time: '' });
                          }}
                        >
                          Abbrechen
                        </Button>
                      </div>
                      {addOption.isError && (
                        <Alert>
                          {addOption.error instanceof ApiError ? addOption.error.message : 'Konnte nicht hinzugefügt werden.'}
                        </Alert>
                      )}
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      className="gap-1.5 px-2.5 py-1.5 text-xs"
                      onClick={() => setAddingOption(true)}
                    >
                      <Plus size={13} /> Termin hinzufügen
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
          {!poll && !canManage && (
            <p className="text-sm text-slate-500">Für dieses Spiel läuft noch keine Terminabstimmung.</p>
          )}
        </Step>

        {canManage && (
          <Step n={2} done={step2Done} title="Geeinigt auf">
            <div className="flex items-end gap-2">
              <div className="grid flex-1 grid-cols-2 gap-2">
                <Field label="Datum">
                  <input
                    type="date"
                    value={agreeDate}
                    onChange={(e) => setAgreeDate(e.target.value)}
                    className={FIELD_CLASS}
                  />
                </Field>
                <Field label="Uhrzeit (optional)">
                  <input
                    type="time"
                    value={agreeTime}
                    onChange={(e) => setAgreeTime(e.target.value)}
                    className={FIELD_CLASS}
                  />
                </Field>
              </div>
            </div>
            <Button
              type="button"
              className={`mt-2 w-full ${FIELD_HEIGHT} px-3 text-sm`}
              disabled={!agreeDate || agree.isPending}
              onClick={() => agree.mutate()}
            >
              {agree.isPending ? 'Wird gespeichert …' : 'Termin festlegen'}
            </Button>
            {agree.isError && (
              <Alert>{agree.error instanceof ApiError ? agree.error.message : 'Konnte nicht gespeichert werden.'}</Alert>
            )}
          </Step>
        )}
      </div>
    </Modal>
  );
}
